/**
 * The profile's marks on the surface — docs/27 §3.1, and §12 item 1.
 *
 * `RunTerrain` (#117) made digging persist within a run: a run-owned diff log,
 * written by `damageCell`'s hook, replayed over every regenerated chunk. It has
 * one boundary it cannot cross, and the boundary is the design: the log is
 * keyed on the *run seed*, so it dies with the run — which is correct below
 * `y = 0`, where every run is a different world, and wrong above it, where the
 * frame is seed-invariant. The Great Tree is the same tree in every run; a hole
 * dug at its foot was not the same hole, because the only log that knew about
 * it was thrown away with the run that dug it.
 *
 * docs/27 §3.1 names three clocks: the authored frame (pure in nothing, never
 * resets), the surface's dressing (pure in a profile-owned `surfaceSeed`,
 * advanced on a schedule the profile owns), and everything below `y = 0` (pure
 * in the run seed, reset every run). This file is the middle clock's diff log:
 * **`RunTerrain` with a different owner and a different key**, exactly as that
 * section specifies. The class is deliberately near-verbatim — open-span
 * coalescing, a reject box, a guard that drops a mismatched log — because the
 * differences that matter are only the owner (`ProfileData`, not `RunState`)
 * and the key (`surfaceSeed`, not `run.seed`), and any other divergence between
 * the two files would be a bug wearing a refactor.
 *
 * ==== THE PARTITION =====================================================
 *
 * One rule, stated once: **a mark at `y < 0` belongs to the profile; a mark at
 * `y >= 0` belongs to the run.** `SURFACE_MARK_MAX_Y` is that boundary, both
 * recorders filter against it, and every diff-log entry is one row tall (the
 * coalescing rule merges within a row only), so no entry straddles it.
 *
 * Without the partition the same destroyed cell would be recorded twice and
 * replayed twice — harmless while the logs agree, and a world-corrupting race
 * the day one of them is dropped by its guard while the other is not.
 *
 * ==== WHY THE GUARD KEYS ON `surfaceSeed` ===============================
 *
 * The surface frame reads no seed, so a surface mark is valid in *every* run —
 * that is the point of this file. What invalidates it is the dressing clock
 * advancing: §3.1's "minor variations, and occasional bigger differences" are
 * authored changes to the surface (a storm, a fallen bough, the pool frozen),
 * and a profile that advances `surfaceSeed` is declaring the surface renewed.
 * Old holes in a renewed surface are exactly as wrong as another run's holes
 * in a fresh cave, and the guard treats them the same way: drop, loudly enough
 * for a test to see, quietly enough not to take the profile down.
 *
 * The prologue is covered by the same guard `RunTerrain` uses (#118): the game
 * binds neither recorder outside a `world` scene, so an authored chamber's
 * digs are recorded onto nothing.
 */

import { recordCell, recordRect } from '../meta/save';
import type { ProfileData, TerrainDiffEntry } from '../meta/save';
import { applyDiffLog } from '../meta/save';
import { CHUNK_SHIFT, CHUNK_SIZE } from '../sim/world';
import type { CellWorld, ChunkGenerator } from '../sim/world';

/**
 * Marks strictly above this row are the profile's; at or below, the run's.
 * docs/27 §3.1's own boundary — "everything below `y = 0`" resets per run —
 * and not `SURFACE_Y`: the band between `SURFACE_Y` and 0 is authored relief
 * standing on seed-invariant ground, and a hole in it must persist.
 */
export const SURFACE_MARK_MAX_Y = 0;

export class SurfaceTerrain {
  private readonly cells: CellWorld;
  private readonly tickOf: () => number;
  private profile: ProfileData | null = null;

  /** The entry this recorder wrote last, while it is still safe to widen. */
  private open: TerrainDiffEntry | null = null;

  /** Cells recorded since the profile was adopted. Telemetry, and a smell. */
  recorded = 0;
  /** Cells laid back down over regenerated terrain. Telemetry. */
  replayed = 0;
  /**
   * Set when the log's key did not match the profile's dressing clock. The
   * marks belong to a surface that no longer exists — see the header — so they
   * are dropped rather than replayed onto its replacement.
   */
  seedMismatch = false;

  /** Reject box, `RunTerrain`'s argument verbatim: widen-only, rebuilt on bind. */
  private minX = Infinity;
  private minY = Infinity;
  private maxX = -Infinity;
  private maxY = -Infinity;

  constructor(cells: CellWorld, tickOf: () => number) {
    this.cells = cells;
    this.tickOf = tickOf;
    // Chained, not assigned — the hook already carries the rigid-body wake
    // path and `RunTerrain`'s recorder, and both must survive (#42, #117).
    const prev = cells.onCellDamaged;
    cells.onCellDamaged = (x, y) => {
      this.note(x, y);
      if (prev !== null) prev(x, y);
    };
  }

  // ------------------------------------------------------------ recording

  private note(x: number, y: number): void {
    const profile = this.profile;
    if (profile === null) return;
    if (y >= SURFACE_MARK_MAX_Y) return; // the run's, not ours
    const mat = this.cells.peek(x, y);
    const log = profile.surface;
    const tick = this.tickOf();
    const entries = log.entries;
    const open = this.open;
    const last = entries.length > 0 ? entries[entries.length - 1] : null;

    if (
      open !== null &&
      open === last &&
      open.tick === tick &&
      open.y === y &&
      open.mat === mat &&
      x === open.x + open.w
    ) {
      entries.pop();
      recordRect(log, tick, open.x, open.y, open.w + 1, 1, mat);
    } else {
      recordCell(log, tick, x, y, mat);
    }

    // Re-read: `recordCell`/`recordRect` compact past the threshold and
    // replace the array, so the local is stale from here on.
    const now = log.entries;
    this.open = now.length > 0 ? now[now.length - 1] : null;
    this.widen(x, y, 1, 1);
    this.recorded++;
  }

  private widen(x: number, y: number, w: number, h: number): void {
    if (x < this.minX) this.minX = x;
    if (y < this.minY) this.minY = y;
    if (x + w > this.maxX) this.maxX = x + w;
    if (y + h > this.maxY) this.maxY = y + h;
  }

  // -------------------------------------------------------------- binding

  /**
   * Adopt a profile: replay what it already carries, then record into it.
   *
   * Called wherever `RunTerrain.bind` is called, with one difference worth a
   * sentence: the run changes and the profile does not, so across a death this
   * replays the *same* log into a *new* world — which is the sentence "the
   * tree is the same tree" made operational.
   */
  bind(profile: ProfileData): void {
    this.profile = profile;
    this.open = null;
    this.minX = Infinity;
    this.minY = Infinity;
    this.maxX = -Infinity;
    this.maxY = -Infinity;
    this.seedMismatch = profile.surface.seed !== (profile.surfaceSeed >>> 0);
    if (this.seedMismatch) {
      profile.surface.entries.length = 0;
      profile.surface.seed = profile.surfaceSeed >>> 0;
      return;
    }
    for (const e of profile.surface.entries) this.widen(e.x, e.y, e.w, e.h);
    let n = 0;
    applyDiffLog(profile.surface, {
      setCell: (x, y, mat) => {
        // Resident chunks only — `withSurfaceDiffs` covers the rest for free
        // the moment the player goes there, `RunTerrain.bind`'s argument.
        if (this.cells.peekChunkAt(x, y) === null) return;
        this.cells.set(x, y, mat);
        n++;
      },
    });
    this.replayed += n;
  }

  /** Let go of the profile. Only for teardown — a run ending is not this. */
  clear(): void {
    this.profile = null;
    this.open = null;
    this.minX = Infinity;
    this.minY = Infinity;
    this.maxX = -Infinity;
    this.maxY = -Infinity;
  }

  // --------------------------------------------------------------- replay

  /** Lay the profile's marks back over a freshly generated chunk. */
  decorate(cx: number, cy: number): void {
    const profile = this.profile;
    if (profile === null) return;
    const x0 = cx << CHUNK_SHIFT;
    const y0 = cy << CHUNK_SHIFT;
    const x1 = x0 + CHUNK_SIZE;
    const y1 = y0 + CHUNK_SIZE;
    if (x1 <= this.minX || x0 >= this.maxX || y1 <= this.minY || y0 >= this.maxY) return;
    for (const e of profile.surface.entries) {
      if (e.x >= x1 || e.y >= y1 || e.x + e.w <= x0 || e.y + e.h <= y0) continue;
      const ax0 = e.x > x0 ? e.x : x0;
      const ay0 = e.y > y0 ? e.y : y0;
      const ax1 = e.x + e.w < x1 ? e.x + e.w : x1;
      const ay1 = e.y + e.h < y1 ? e.y + e.h : y1;
      for (let y = ay0; y < ay1; y++) {
        for (let x = ax0; x < ax1; x++) {
          this.cells.set(x, y, e.mat);
          this.replayed++;
        }
      }
    }
  }
}

/**
 * The generator decorator. Sits directly inside `withRunDiffs` — the two logs
 * are disjoint by the partition, so their order cannot matter, and both go
 * over every content decorator for `withRunDiffs`'s reason: the player's marks
 * are the last thing that happened to the world.
 */
export function withSurfaceDiffs(inner: ChunkGenerator, terrain: SurfaceTerrain): ChunkGenerator {
  return (chunk, cx, cy) => {
    inner(chunk, cx, cy);
    terrain.decorate(cx, cy);
  };
}
